import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { FormModificationState } from "@/Common/Models/UI/Forms/FormModificationState";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { IMedicalItemsModel } from "@/Patient/Models/UI/MedicalSummary/IMedicalItemsModel";
import { ConditionModel } from "./Models/ConditionModel";
import { ImmunizationModel } from "./Models/ImmunizationModel";
import { ImmunizationProfileModel } from "./Models/ImmunizationProfileModel";
import { RvqLocationPreference } from "./Models/RvqLocationPreference";
import { RvqMedicationPreference } from "./Models/RvqMedicationPreference";
import { RvqPractitionerPreference } from "./Models/RvqPractitionerPreference";
import { VaccinationRefusalModel } from "./Models/VaccinationRefusalModel";
import { GetImmunizationProfileResponse } from "./ResponseModels/GetImmunizationProfileResponse";
import { ImmunizationProfileComponent } from "./ImmunizationProfileComponent";

interface ImmunizationProfileProps {
    mylePatientContactId: string;
    medicationPreferences: RvqMedicationPreference[];
    practitionerPreferences: RvqPractitionerPreference[];
    locationPreferences: RvqLocationPreference[];
    onUpdatePreferences: (
        preferences: RvqMedicationPreference[] | null,
        practitionerPreferences: RvqPractitionerPreference[] | null,
        locationPreferences: RvqLocationPreference[] | null,
        then?: () => void
    ) => void;
    refreshVaccinationValues: () => void;
    onClose: () => void;
    model: IMedicalItemsModel;
}

enum ImmunizationProfileInteractionPopupType {
    None,
    OverrideVaccinationStatus,
    ImmunityProof,
    CounterindicationOrPrecaution,
    ImmunizationRefusal,
    ImmunityDeletion,
    Preferences,
    ViewVaccination
}

class ImmunizationProfileState {
    public isLoading: boolean;
    public currentPopup: ImmunizationProfileInteractionPopupType;
    public formMode: FormModificationState;
    public immunizationProfileModel: ImmunizationProfileModel;
    public messages: string[];
    public currentCondition: ConditionModel;
    public currentImmunization: ImmunizationModel;
    public isProfileReadOnly: boolean;
    public isCorrector: boolean;
    public immunizationDeletePermissions: number[];
    public currentVaccinationRefusal: VaccinationRefusalModel;

    public constructor(init?: Partial<ImmunizationProfileState>) {
        Object.assign(this, init);
    }

    public static default(): ImmunizationProfileState {
        return new ImmunizationProfileState({
            isLoading: false,
            currentPopup: ImmunizationProfileInteractionPopupType.None,
            formMode: FormModificationState.None,
            messages: [],
            currentCondition: null,
            currentImmunization: null,
            isProfileReadOnly: true,
            currentVaccinationRefusal: null
        });
    }
}

class ImmunizationProfileController extends BaseReactComponent<ImmunizationProfileProps, ImmunizationProfileState> {
    public constructor(props: ImmunizationProfileProps) {
        super(props);
        this.state = ImmunizationProfileState.default();
    }

    public async componentDidMount(): Promise<void> {
        // fetch immunization profile

        const response = await Client.postAsync<GetImmunizationProfileResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_GetImmunizationProfile,
            {
                PatientContactId: this.props.mylePatientContactId
            }
        );

        const isBeingMerged = response?.data?.ImmunizationProfile?.Patient.IsBeingMerged;
        this.mutateState(s => {
            s.immunizationProfileModel = response?.data?.ImmunizationProfile;
            s.isCorrector = response.data?.IsCorrector;
            s.immunizationDeletePermissions = response.data?.ImmunizationDeletePermissions;
            s.isProfileReadOnly = !response.isSuccess || isBeingMerged;
            s.messages = response.isSuccess
                ? isBeingMerged
                    ? [cacheStore.GetString("QuebecVaccinationRegistry_PatientBeingMergedMessage")]
                    : []
                : response.errors.map(x => x.Message);
        });

        this.props.refreshVaccinationValues();
    }

    public render(): JSX.Element {
        return (
            <ImmunizationProfileComponent
                parentState={this.state}
                mutateParentState={(mutation, then) => this.mutateState(mutation, then)}
                immunizationProfileModel={this.state.immunizationProfileModel}
                updateImmunizationProfileModel={(model, appliedLocation) => this.mutateState(
                    s => s.immunizationProfileModel = model,
                    () => {
                        if (appliedLocation) {
                            const preferences = this.props.locationPreferences.map(x => new RvqLocationPreference(x));
                            const index = preferences.indexOfObject("Id", appliedLocation.EntityId);
                            if (index > -1) {
                                preferences[index] = new RvqLocationPreference(appliedLocation);
                                this.props.onUpdatePreferences(null, null, preferences);
                            }
                        }
                    }
                )}
                {...this.props}
            />
        );
    }
}

export { ImmunizationProfileController, ImmunizationProfileInteractionPopupType, ImmunizationProfileProps, ImmunizationProfileState };
